"use client";
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Check, Database, Globe, Lock, Zap, CreditCard, MessageSquare } from "lucide-react";

const STEPS = [
  {
    id: 1,
    title: "Define",
    description:
      "Describe the automation you need in plain English. Our AI understands your intent and sets up the foundation.",
  },
  {
    id: 2,
    title: "Connect",
    description:
      "Securely link your applications and data sources with our pre-built integrations.",
  },
  {
    id: 3,
    title: "Automate",
    description:
      "Activate your workflow and watch as tasks are completed automatically, saving you time and effort.",
  },
];

const INTEGRATION_ICONS = [
  { id: "postgres", label: "PostgreSQL", icon: Database, color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  { id: "auth0", label: "Auth0", icon: Lock, color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20" },
  { id: "stripe", label: "Stripe", icon: CreditCard, color: "text-purple-500", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  { id: "slack", label: "Slack", icon: MessageSquare, color: "text-green-500", bg: "bg-green-500/10", border: "border-green-500/20" },
];

const WorkflowSection = () => {
  const [activeStep, setActiveStep] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (latest < 0.35) {
      setActiveStep(1);
    } else if (latest < 0.70) {
      setActiveStep(2);
    } else {
      setActiveStep(3);
    }
  });

  const handleStepClick = (id: number) => {
    if (!containerRef.current) return;

    const containerHeight = containerRef.current.offsetHeight;
    const windowHeight = window.innerHeight;
    const scrollableDistance = containerHeight - windowHeight;

    let targetProgress = 0;
    if (id === 1) targetProgress = 0.25;
    else if (id === 2) targetProgress = 0.52;
    else if (id === 3) targetProgress = 0.95;

    const targetScrollY = containerRef.current.offsetTop + (scrollableDistance * targetProgress);

    window.scrollTo({
      top: targetScrollY,
      behavior: "smooth",
    });
  };

  return (
    <section ref={containerRef} className="relative h-[400vh] bg-background">
      <div className="sticky top-0 h-screen flex flex-col justify-center pt-20 pb-30 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-primary-text mb-4 tracking-tight">
              How it Works in 3 Easy Steps
            </h2>
            <p className="text-lg text-secondary-text max-w-2xl mx-auto">
              Our platform simplifies backend automation into a straightforward,
              three-step process. Define your task, connect your services, and let
              our AI handle the rest.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Column: Steps Navigation */}
            <div className="space-y-8">
              {STEPS.map((step) => (
                <div
                  key={step.id}
                  onClick={() => handleStepClick(step.id)}
                  className={`cursor-pointer transition-all duration-300 group relative pl-6 border-l-2 ${activeStep === step.id
                    ? "border-primary opacity-100"
                    : "border-border opacity-40 hover:opacity-60"
                    }`}
                >
                  <div className="flex items-baseline gap-4 mb-2">
                    <span className="text-2xl font-bold text-primary-text/50">
                      0{step.id}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-primary-text">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-base md:text-lg text-secondary-text leading-relaxed max-w-md">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Right Column: Visualizations */}
            <div className="relative h-[400px] lg:h-[450px] w-full bg-surface rounded-3xl border border-border p-8 shadow-2xl flex items-center justify-center overflow-hidden">
              <AnimatePresence mode="wait">
                {activeStep === 1 && <Step1Visualization key="step1" />}
                {activeStep === 2 && <Step2Visualization key="step2" />}
                {activeStep === 3 && <Step3Visualization key="step3" />}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Sub-components for cleaner code
const Step1Visualization = () => {
  const text = "Understood. I will trigger on 'payment_succeeded' and execute 'createUser'.";
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      setDisplayedText(text.slice(0, i));
      i++;
      if (i > text.length) clearInterval(timer);
    }, 30);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className="w-full max-w-md space-y-4"
    >
      <div className="flex items-center justify-between text-xs text-secondary-text mb-4">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span>AI Chat</span>
      </div>

      {/* User Message */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className="flex justify-end"
      >
        <div className="bg-black text-white px-5 py-3 rounded-2xl rounded-tr-sm text-sm max-w-[85%] shadow-lg">
          Create a new user in the database whenever a Stripe payment is successful.
        </div>
      </motion.div>

      {/* AI Response */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.8 }}
        className="flex justify-start"
      >
        <div className="bg-hover-background text-primary-text px-5 py-3 rounded-2xl rounded-tl-sm text-sm max-w-[85%] shadow-sm border border-border min-h-[80px]">
          <p className="font-mono text-xs leading-relaxed">
            {displayedText}
            <span className="animate-pulse">|</span>
          </p>
        </div>
      </motion.div>

      {/* Input Area */}
      <div className="mt-8 relative">
        <div className="w-full bg-hover-background h-10 rounded-full border border-border flex items-center px-4 text-sm text-secondary-text">
          Describe your task...
        </div>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-text/30">
          <Zap className="w-4 h-4" />
        </div>
      </div>
    </motion.div>
  );
};

const Step2Visualization = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative w-full h-full flex items-center justify-center"
    >
      {/* Central Hub */}
      <motion.div
        initial={{ scale: 2.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, ease: "backOut" }}
        className="relative z-20 w-24 h-24 bg-surface rounded-2xl flex items-center justify-center border border-border shadow-xl"
      >
        <Globe className="w-10 h-10 text-primary" />
      </motion.div>

      {/* Orbiting Icons */}
      {INTEGRATION_ICONS.map((item, index) => {
        const total = INTEGRATION_ICONS.length;
        const radius = 140; // Distance from center
        const angle = (index / total) * 2 * Math.PI - Math.PI / 2; // Start from top
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;

        return (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
            animate={{ opacity: 1, scale: 1, x, y }}
            transition={{ delay: 0.6 + index * 0.1, type: "spring", stiffness: 200, damping: 20 }}
            className="absolute z-10"
          >
            <div className="group relative flex flex-col items-center">
              <div
                className={`w-14 h-14 rounded-xl flex items-center justify-center border backdrop-blur-sm transition-transform hover:scale-110 cursor-pointer ${item.bg} ${item.border}`}
              >
                <item.icon className={`w-6 h-6 ${item.color}`} />
              </div>

              {/* Hover Label */}
              <div className="absolute top-full mt-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none">
                {item.label}
              </div>

              {/* Connecting Line to Center */}
              <motion.div
                className="absolute top-1/2 left-1/2 h-0.5 bg-border -z-10"
                style={{
                  width: radius - 40, // Adjust length to not overlap icons
                  left: "50%",
                  top: "50%",
                }}
                initial={{ scaleX: 0, rotate: (angle * 180) / Math.PI + 180, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
              />
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

const Step3Visualization = () => {
  return (
    <motion.div
      className="flex flex-col items-center justify-center"
    >
      <div className="relative">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="w-32 h-32 bg-green-500 rounded-full flex items-center justify-center shadow-2xl shadow-green-500/30"
        >
          <motion.div
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Check className="w-16 h-16 text-white" strokeWidth={4} />
          </motion.div>
        </motion.div>

        {/* Pulsing Rings */}
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="absolute inset-0 rounded-full border-2 border-green-500"
            initial={{ scale: 1, opacity: 0.8 }}
            animate={{ scale: 2, opacity: 0 }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.4,
              ease: "easeOut"
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-12 text-center"
      >
        <h4 className="text-2xl font-bold text-primary-text mb-2">Workflow Active</h4>
        <p className="text-secondary-text">System running optimally</p>
      </motion.div>
    </motion.div>
  );
};

export default WorkflowSection;